import { useEffect, useMemo, useState } from 'react';

import { FAIconName, useToastMessage } from '@equip.health/ui';

import { useGetPatientTagsEnumQuery } from '~/store/services/enumerations';

import {
  convertToUSDateFormat,
  getIsShippingAddressFieldEmpty,
  getPatientEditPayloadData,
} from '../util/patient.util';
import util from '../util/util';
import { formatPhoneNumberWithCode } from '../util/patientContact.util';

import {
  usePutBasicInfoMutation,
  usePutTagsInfoMutation,
} from '~/store/services/patients';
import { EditBasicInfoFormState } from '~/lib/types/patient/EditBasicInfoFormState';

export const useEditBasicInfoForm = (
  basicInfo: Patient,
  patientId: string,
  onUpdate?: () => void,
) => {
  const [putBasicInfo, putBasicInfoResult] = usePutBasicInfoMutation();
  const [putTagsInfo, putTagsInfoResult] = usePutTagsInfoMutation();
  const { data: tagValues } = useGetPatientTagsEnumQuery();
  const { createToast } = useToastMessage();

  const personalInfoDataKeys: (keyof ProfilePersonalInfoData)[] = [
    'firstName',
    'lastName',
    'dateOfBirth',
  ];

  const [formData, setFormData] = useState<EditBasicInfoFormState>({
    demographics: {
      ethinicities: basicInfo.ethnicities,
      firstHeardAboutEquipExtId: basicInfo.firstHeardAboutEquipExtId,
      genderOffRecord: basicInfo.genderOffRecord,
      genderOnRecord: basicInfo.genderOnRecord,
      hasGivenRecordingConsent: basicInfo.hasGivenRecordingConsent,
      languagesSpoken: basicInfo.languages,
      patientTags: basicInfo.patientTags,
      pronounsOffRecord: basicInfo.pronounsOffRecord,
      pronounsOnRecord: basicInfo.pronounsOnRecord,
      sex: basicInfo.sex,
    },
    emailChangeWarningModalOpen: false,
    hasUnsavedChanges: false,
    isError: putBasicInfoResult.isError,
    isLoading: false,
    mainAddress: {
      addressLine1: basicInfo.addressLine1,
      addressLine2: basicInfo.addressLine2,
      city: basicInfo.city,
      country: basicInfo.country,
      county: basicInfo.county,
      latitude: basicInfo.latitude,
      longitude: basicInfo.longitude,
      name: basicInfo.affiliationName,
      state: basicInfo.state,
      timezone: basicInfo.timezone,
      zipCode: basicInfo.zip,
    },
    personalInfo: {
      chosenName: basicInfo.chosenName,
      contactPreferences: basicInfo.contactPreferences,
      dateOfBirth: basicInfo.dateOfBirth
        ? convertToUSDateFormat(basicInfo.dateOfBirth)
        : null,
      email: basicInfo.email,
      firstName: basicInfo.firstName,
      invalidFields: {
        dateOfBirth: false,
        email: false,
        firstName: false,
        lastName: false,
        phonesField: false,
      },
      isDuplicateEmail: false,
      lastName: basicInfo.lastName,
      mrn: basicInfo.mrn,
    },
    phonesField: {
      extension: basicInfo.officeExtension,
      fax: formatPhoneNumberWithCode(basicInfo.faxNumber),
      home: formatPhoneNumberWithCode(basicInfo.homePhone),
      mobile: formatPhoneNumberWithCode(basicInfo.phoneNumber),
      office: formatPhoneNumberWithCode(basicInfo.officeNumber),
      other: formatPhoneNumberWithCode(basicInfo.secondaryPhoneNumber),
    },
    shippingAddress: {
      addressLine1: basicInfo.shippingAddress?.addressLine1,
      addressLine2: basicInfo.shippingAddress?.addressLine2,
      city: basicInfo.shippingAddress?.city,
      country: basicInfo.shippingAddress?.country,
      county: basicInfo.shippingAddress?.county,
      latitude: basicInfo.shippingAddress?.latitude,
      longitude: basicInfo.shippingAddress?.longitude,
      name: basicInfo.shippingAddress?.affiliationName,
      state: basicInfo.shippingAddress?.state,
      zipCode: basicInfo.shippingAddress?.zip,
    },
    userId: basicInfo.userId,
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      isLoading: putBasicInfoResult.isLoading || putTagsInfoResult.isLoading,
    }));
  }, [putBasicInfoResult.isLoading, putTagsInfoResult.isLoading]);

  const isShippingAddressFieldEmpty = getIsShippingAddressFieldEmpty(
    formData.shippingAddress,
  );

  const shouldWarnEmailChange = useMemo(() => {
    return (
      basicInfo?.hasAuthId &&
      formData.personalInfo.email &&
      formData.personalInfo.email !== basicInfo?.email &&
      basicInfo?.isAccountActive
    );
  }, [basicInfo, formData.personalInfo.email]);

  const updatePersonalInfo = (
    field: keyof ProfilePersonalInfoData | 'phonesField',
    value: string | string[] | boolean | PhonesFieldType,
    isInvalid?: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      hasUnsavedChanges: true,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value,
        invalidFields: {
          ...prev.personalInfo.invalidFields,
          [field]: isInvalid ?? prev.personalInfo.invalidFields[field],
        },
      },
    }));
  };

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      hasUnsavedChanges: true,
    }));
  };

  type EditableSections = Omit<
    EditBasicInfoFormState,
    | 'userId'
    | 'isLoading'
    | 'isError'
    | 'emailChangeWarningModalOpen'
    | 'hasUnsavedChanges'
    | 'personalInfo'
  >;

  const updateSection = <T extends keyof EditableSections>(
    section: T,
    updatedData: EditableSections[T],
  ) => {
    setFormData((prev) => ({
      ...prev,
      hasUnsavedChanges: true,
      [section]: {
        ...prev[section],
        ...updatedData,
      },
    }));
  };

  const validateRequiredFields = (): boolean => {
    const updatedInvalidFields = { ...formData.personalInfo.invalidFields };
    const requiredFields = basicInfo?.email
      ? [...personalInfoDataKeys, 'email']
      : personalInfoDataKeys;

    requiredFields.forEach((field) => {
      updatedInvalidFields[field] = !formData.personalInfo[field];
    });

    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        invalidFields: updatedInvalidFields,
      },
    }));

    return Object.values(updatedInvalidFields).every((isValid) => !isValid);
  };

  const handleSaveForm = () => {
    if (shouldWarnEmailChange && !formData.emailChangeWarningModalOpen) {
      updateField('emailChangeWarningModalOpen', true);
      return;
    }

    updateField('emailChangeWarningModalOpen', false);
    const isShippingAddressValid =
      isShippingAddressFieldEmpty || Boolean(formData.shippingAddress.state);
    const isValid =
      validateRequiredFields() &&
      isShippingAddressValid &&
      formData.mainAddress.state;

    if (!isValid) return;

    const payloadData = getPatientEditPayloadData(
      formData,
      isShippingAddressFieldEmpty,
    );

    putBasicInfo({
      patientBody: payloadData,
      patientId,
    })
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'edit-basic-info-toast',
          text: 'Basic profile updated',
          variant: 'success',
        });
        onUpdate();
      })
      .catch((error) => {
        if (
          typeof error?.data === 'string' &&
          error?.data?.includes('Duplicate email found')
        ) {
          setFormData((prev) => ({
            ...prev,
            personalInfo: {
              ...prev.personalInfo,
              isDuplicateEmail: true,
            },
          }));
        } else {
          updateField('isError', true);
        }
      });
    if (
      JSON.stringify(basicInfo?.patientTags) !==
      JSON.stringify(formData.demographics?.patientTags)
    ) {
      putTagsInfo({
        patientId,
        tags: util.getTagCodes(formData.demographics?.patientTags, tagValues),
      });
    }
  };

  return {
    formData,
    handleSaveForm,
    updateField,
    updatePersonalInfo,
    updateSection,
    validateRequiredFields,
  };
};
